/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { forwardRef } from "react";

import { cva, cx } from "@panda/css";

const cardThumbnailStyles = cva({
	base: {
		aspectRatio: "1/1",
		overflow: "hidden",
		position: "relative",
		width: "100%",
		borderRadius: "var(--cosmos-card__thumbnail-radius)",
		"&:has(> .cosmos-card__thumbnail-overlay)": {
			borderRadius: "var(--cosmos-card-radius)",
			"& .cosmos-card__thumbnail-overlay": {
				borderRadius: "var(--cosmos-card-radius)",
			},
		},

		"& img, & video": {
			height: "100%",
			inset: 0,
			objectFit: "cover",
			objectPosition: "center",
			position: "absolute",
			transition: "transform token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
			width: "100%",
			zIndex: 0,
		},
	},

	variants: {
		aspectRatio: {
			"16/9": {
				aspectRatio: "16/9",
			},
			"5/3": {
				aspectRatio: "5/3",
			},
			"1/1": {
				aspectRatio: "1/1",
			},
			unset: {
				aspectRatio: "unset",
			},
		},
	},
});

export type CosmosCardThumbnailProps = {
	aspectRatio?: "16/9" | "5/3" | "1/1" | "unset";
	className?: string;
	children: React.ReactNode;
};

export const CosmosCardThumbnail = forwardRef(
	(
		{ aspectRatio, className, children, ...props }: CosmosCardThumbnailProps,
		forwardedRef: React.Ref<HTMLDivElement>,
	): React.JSX.Element => {
		return (
			<div
				{...props}
				className={cx("cosmos-card__thumbnail", cardThumbnailStyles({ aspectRatio }), className)}
				ref={forwardedRef}
			>
				{children}
			</div>
		);
	},
);

CosmosCardThumbnail.displayName = "CosmosCard.Thumbnail";
